/* eslint-disable max-classes-per-file */
import { DatePipe, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    OnInit,
    QueryList,
    ViewChildren,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { BlockComponent } from "@components/container-block/block.component";
import { PageComponent } from "@components/page/page.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import { combineLatest } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { Handelssortimentsgrupp } from "~app/common/models/constants";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { ForkalkylsmallarService } from "../services/forkalkylsmallar.service";
import { ForkalkylsmallComponent } from "./components/forkalkylsmall/forkalkylsmall.component";
import { ForkalkylsmallDTO } from "./models/ForkalkylsmallDTO";
import { ForkalkylsmallQueryResponseDTO } from "./models/ForkalkylsmallQueryResponseDTO";
import { IForkalkylsmallBasdata } from "./models/IForkalkylsmallBasdata";
import { TilldelaHandelssortimentsGruppDTO } from "./models/TilldelaHandelssortimentsGruppDTO";

@Component({
    selector: "rask-forkalkylsmallar",
    styleUrls: ["./forkalkylsmallar.component.scss"],
    templateUrl: "./forkalkylsmallar.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        NgTemplateOutlet,
        BlockComponent,
        TogglePanelComponent,
        ForkalkylsmallComponent,
        DatePipe
    ]
})
export class ForkalkylsmallarComponent implements OnInit {
    activeModal = inject(NgbActiveModal);
    mallar: ForkalkylsmallQueryResponseDTO[];
    isModalActive = false;
    basdata: IForkalkylsmallBasdata;
    @ViewChildren("mallcomponents") mallcomponents: QueryList<ForkalkylsmallComponent>;

    private readonly forkalkylsmallarService = inject(ForkalkylsmallarService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly busyService = inject(BusyService);

    private lastUpdatedMallID: number;

    dismiss() {
        if (this.dirtyForm.canClose()) {
            this.activeModal.dismiss();
        }
    }

    isModalDirty(): boolean {
        return this.mallcomponents.some(x => x.isDirty());
    }

    getDirtyStatus(mallIndex: number): boolean {
        if (this.mallcomponents) {
            const found = this.mallcomponents.toArray()[mallIndex];
            if (found) {
                return found.mallIsDirty;
            }
        }
        return false;
    }

    isMallDefaultExpanded(mall: { id: number }): boolean {
        if (mall.id < 1) {
            return true;
        }

        if (mall.id === this.lastUpdatedMallID) {
            return true;
        }
        return false;
    }

    ngOnInit() {

        const work = combineLatest([
            this.forkalkylsmallarService.getAllMallar(),
            this.forkalkylsmallarService.handelssortimentGruppLista,
            this.forkalkylsmallarService.mattslaglist,
            this.forkalkylsmallarService.getAllProduktionsOmraden()
        ]);

        this.busyService.addWork(work).subscribe(([
            responseMallar,
            responseHSGrupper,
            responseMattslag,
            responseProduktionsomraden
        ]) => {
            this.mallar = this.sortMallar(responseMallar);
            this.basdata = {
                forkalkylsmallar: this.mallar,
                handelssortimentGruppLista: this.removeNonApplicableHSGroups(responseHSGrupper),
                mattslagList: responseMattslag,
                produktionsomraden: responseProduktionsomraden
            };
        });

        this.dirtyForm.onDirtyNgModel(
            this.isModalDirty.bind(this),
            this.saveAll.bind(this),
            d => this.dirtyCheckCallback(d),
            () => this.canMallarBeSaved(),
            "Osparade förändringar",
            "En eller flera mallar har osparade förändringar",
            { size: "lg",
                centered: false });
    }

    removeNonApplicableHSGroups(grupper: TilldelaHandelssortimentsGruppDTO[]) {
        return grupper.filter(hsg =>
            hsg.kod !== Handelssortimentsgrupp.Grot &&
            hsg.kod !== Handelssortimentsgrupp.Stubbar &&
            hsg.kod !== Handelssortimentsgrupp.Ovrigt);
    }

    canMallarBeSaved(): boolean {
        const result = !this.mallcomponents.some(x => !x.canBeSaved());
        this.changeDetector.detectChanges();
        return result;
    }

    dirtyCheckCallback(result: IDirtyFormResult): void {

        if (result.isSkipSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }

        if (result.isSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }
    }

    sortMallar(mallar: ForkalkylsmallQueryResponseDTO[]): ForkalkylsmallQueryResponseDTO[] {
        return mallar.sort((a, b) => moment(b.uDate).unix() - moment(a.uDate).unix());
    }

    isAllowedToAddRows(): boolean {
        return ((this.mallar === null || this.mallar === undefined || this.mallar.length === 0) || this.mallar.every(mall => mall.id > 0));
    }

    saveAll() {
        this.mallcomponents.forEach(x => x.saveAll(false));
    }

    addMall = () => {
        if (this.isAllowedToAddRows()) {
            const mall = {
                id : 0,
                handelssortiment : [],
                namn : ""
            } as ForkalkylsmallQueryResponseDTO;
            this.mallar.unshift(mall);
        }
    };

    refresh(refreshItem: IForkalkylRefreshItem) {
        if (refreshItem.mode === ForkalkylRefreshMode.Delete) {
            this.deleteModifiedMall(refreshItem.mall);
            this.sortMallar(this.mallar);
            this.basdata.forkalkylsmallar = this.mallar;
        } else {
            this.busyService.addWork(this.forkalkylsmallarService.getAllMallar()).subscribe(mallarFromDB => {
                this.saveModifiedMall(refreshItem.mall, mallarFromDB);
                this.sortMallar(this.mallar);
                this.basdata.forkalkylsmallar = this.mallar;
            });
        }

    }
    private saveModifiedMall(mall: ForkalkylsmallDTO, mallarFromDB: ForkalkylsmallQueryResponseDTO[]) {
        if (mall.id === 0) {
            this.mallar = this.mallar.filter(cmall => cmall.id !== 0);
            mallarFromDB.filter(nmall => this.mallar.every(cmall => cmall.id !== nmall.id))
                .forEach(nmall => {
                    this.mallar.push(nmall);
                    this.lastUpdatedMallID = nmall.id;
                }
                );
        } else {
            this.mallar = this.mallar.filter(cmall => cmall.id !== mall.id);
            const modifiedFromServer = mallarFromDB.find(nmall => nmall.id === mall.id);
            this.lastUpdatedMallID = modifiedFromServer.id;
            this.mallar.push(modifiedFromServer);
        }
    }

    private deleteModifiedMall(mall: ForkalkylsmallDTO) {
        this.mallar = this.mallar.filter(cmall => cmall.id !== mall.id);
    }

}
export interface IForkalkylRefreshItem {
    mall: ForkalkylsmallDTO;
    mode: ForkalkylRefreshMode;
}
export class ForkalkylRefreshMode {

    // eslint-disable-next-line sonarjs/public-static-readonly
    static Save = "Save";

    // eslint-disable-next-line sonarjs/public-static-readonly
    static Delete = "Delete";
}
